import { useSelect } from "downshift";
import { Check, ChevronDown } from "lucide-react";
import React, { forwardRef, useEffect, useState } from "react";

import { cn } from "@/lib/utils";
import { useHaptics } from "@/modules/haptics/useHaptics/useHaptics";

export type SelectProps = {
  readonly options: SelectOption[];
  readonly onSelect: (selected: SelectOption | null) => void;
  readonly placeholder?: string;
  readonly value?: SelectOption;
  readonly direction?: "up" | "down";
  readonly "aria-invalid"?: boolean;
  readonly "aria-describedby": string;
  readonly id: string;
  readonly disabled?: boolean;
  readonly showAddButton?: boolean;
  readonly addButtonText?: string;
  readonly onAddNewOption?: () => void;
  readonly className?: string;
  readonly hapticsDisabled?: boolean;
  readonly variant?: "default" | "v3";
};

export type SelectOption<T = unknown> = {
  label: string;
  value: T;
  disabled?: boolean;
  supportingText?: string;
} & Partial<{ [key: string]: unknown }>;

const Select = forwardRef<HTMLDivElement, SelectProps>(function Select(
  {
    options,
    onSelect,
    placeholder,
    value,
    direction = "down",
    id,
    disabled = false,
    showAddButton = false,
    addButtonText = "Add New",
    onAddNewOption,
    className,
    hapticsDisabled,
    variant = "default",
    ...props
  },
  ref
): React.ReactElement {
  const { selection } = useHaptics();
  const [selectedItem, setSelectedItem] = useState<
    SelectOption | null | undefined
  >(value);

  useEffect(() => {
    setSelectedItem(value);
  }, [value]);

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getItemProps,
    highlightedIndex,
  } = useSelect({
    items: options,
    itemToString: (item) => (item ? item.label : ""),
    onSelectedItemChange: ({ selectedItem: newSelectedItem }) => {
      setSelectedItem(newSelectedItem);
      void selection({ disabled: hapticsDisabled }).catch(() => undefined);
      onSelect(newSelectedItem);
    },
  });

  const toggleProps = getToggleButtonProps({
    onClick: () => {
      void selection({ disabled: hapticsDisabled }).catch(() => undefined);
    },
  });

  const renderHeader = () => (
    <div className={cn("relative", "rounded-xs")} data-testid="Select_header">
      <input
        {...toggleProps}
        aria-describedby={props["aria-describedby"]}
        aria-invalid={props["aria-invalid"]}
        className={cn(
          "peer block h-10 w-full flex-1 cursor-pointer appearance-none rounded px-3",
          "bg-white text-sm transition-colors ease-in-out",
          "-ring-offset-1 border-none ring-1 ring-light-grey",
          "hover:ring-gray-400",
          "aria-invalid:ring-1 aria-invalid:ring-red aria-invalid:placeholder:text-red",
          "focus:outline-none focus:ring-1 focus:!ring-blue-400 focus:placeholder:!text-secondary",
          "[color-scheme:light]",
          disabled && "cursor-not-allowed opacity-50",
          variant === "v3" ? "h-[56px] rounded-xl" : "h-10 rounded bg-white",
          className
        )}
        disabled={disabled}
        id={id}
        placeholder={placeholder || " "}
        readOnly={true}
        value={selectedItem?.label ?? ""}
      />

      <div
        className={cn(
          "pointer-events-none absolute right-0 top-1/2 flex -translate-y-1/2 cursor-pointer items-center justify-center pr-3"
        )}
      >
        <ChevronDown
          aria-invalid={props["aria-invalid"]}
          aria-label={isOpen ? "Close menu" : "Open menu"}
          className={cn(
            isOpen ? "rotate-180" : "rotate-0",
            "h-6 w-6 transform fill-current transition-all duration-200 ease-in-out aria-invalid:fill-red",

            props["aria-invalid"] ? "red" : ""
          )}
          data-testid="dropdown-toggle-arrow"
        />
      </div>
    </div>
  );

  const renderOptionsList = () => (
    <ul
      className={cn(
        "absolute left-0 right-0 z-20 max-h-[150px] overflow-y-auto rounded-sm border border-gray-300 bg-white shadow-sm",
        direction === "up"
          ? "bottom-auto top-[-5px] -translate-y-full"
          : "bottom-[-5px] translate-y-full",
        variant === "v3" &&
          "absolute max-h-[200px] overflow-y-auto rounded-lg border border-gray-200 bg-white"
      )}
    >
      {options.map((item, index) => {
        const isSelected = selectedItem?.value === item.value;
        const isHighlighted = highlightedIndex === index;
        const isDisabled = item.disabled;
        return (
          <li
            className={cn(
              "flex h-10 cursor-pointer items-center justify-between p-2 transition-colors",
              isSelected && "bg-gray/50",
              isHighlighted && !isSelected && "bg-gray",
              isDisabled && "cursor-not-allowed opacity-50",
              variant === "v3" ? "text-body2" : "text-body1"
            )}
            key={`${item.value}${index}`}
            {...getItemProps({ item, index })}
          >
            {item.label}
            {item.supportingText && (
              <p className="text-xs text-gray-500">{item.supportingText}</p>
            )}
            {isSelected && <Check className="h-4 w-4" />}
          </li>
        );
      })}

      {showAddButton && onAddNewOption && (
        <li className="mt-2 border-t border-gray-200 pt-2">
          <button
            className="flex h-10 w-full cursor-pointer items-center justify-center rounded px-2 text-sm text-[#068762] transition-colors hover:bg-[#068762]/80 hover:text-white"
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onAddNewOption();
            }}
          >
            + {addButtonText}
          </button>
        </li>
      )}
    </ul>
  );

  return (
    <div
      className={cn("Select relative h-10 w-full", variant === "v3" && "z-50")}
      data-testid="Select"
      ref={ref}
    >
      {renderHeader()}
      <div {...getMenuProps()}>{isOpen && renderOptionsList()}</div>
    </div>
  );
});

export default Select;

export function getSelectOptionForKey<T>(
  key: string,
  options: SelectOption<T>[]
): SelectOption<T> | undefined {
  return options.find((option) => option.value === key);
}
